import { getLocaleStore } from '@/i18n';
import { transformFoundationVectorLayerDataToClient } from '@/utils/transform-kit';
import {
    type ILayerBehavior,
    type ILayerProps,
    type IRenderLayerExtraProps,
    type IVectorLayerProps,
} from '@/version-types';
import ImageContent from '../views/image-content';
import VectorLayer from '../views/vector-layer';

type Behaviors = Pick<ILayerBehavior, 'render' | 'getMenuOverlapName' | 'getMenuOverlapIcon' | 'onBeforeAddOrUpdate'>;

export const createRendererBehaviors = (identity: string) => {
    const behaviors: Behaviors = {
        render(layer: ILayerProps, extraProps: IRenderLayerExtraProps) {
            return <VectorLayer layer={layer} {...extraProps} />;
        },

        getMenuOverlapName(_: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.vector);
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            return (
                <div className="l-layer__icon border">
                    <div
                        style={{
                            transform: `scale(${Math.min(16 / layer.width, 16 / layer.height)})`,
                        }}
                    >
                        <ImageContent asMenuIcon layer={layer} identity={identity} isNavigator={false} />
                    </div>
                </div>
            );
        },

        onBeforeAddOrUpdate(layer) {
            const maybeFoundationLayerData = layer.data as IVectorLayerProps;

            const { textInfo } = maybeFoundationLayerData;
            if (textInfo && textInfo.text) {
                maybeFoundationLayerData.textInfo!.text = maybeFoundationLayerData.textInfo!.text.replace(
                    /[\x00-\x09\x0b-\x1f\x7f-\x9f]/gi,
                    '',
                );
            }

            const runtimeData = layer.__runtime || {};
            if (!runtimeData.isInit) {
                layer.data = transformFoundationVectorLayerDataToClient(maybeFoundationLayerData);
            }

            layer.__runtime = { ...runtimeData, isInit: true };

            // 确保 proactiveWidth 和 proactiveHeight 有值
            if (typeof layer.__proactiveWidth === 'undefined') layer.__proactiveWidth = layer.width;
            if (typeof layer.__proactiveHeight === 'undefined') layer.__proactiveHeight = layer.height;

            const layerData = layer.data as IVectorLayerProps;

            // 初始化 effect
            if (!layerData.effect) {
                layerData.effect = { shadow: [], reflection: [] };
            }

            if (!layerData.effect.reflection) layerData.effect.reflection = [];
            if (!layerData.effect.shadow) layerData.effect.shadow = [];

            // 修正 blur
            if (layerData.blur < 0) layerData.blur = 0;

            // 初始化 borderRadius
            if (!layerData.borderRadius) {
                const radius = layerData.radius;
                layerData.borderRadius = {
                    leftBottom: radius,
                    rightBottom: radius,
                    rightTop: radius,
                    leftTop: radius,
                };
            }

            // 初始化文本信息内边距
            if (layerData.textInfo && !layerData.textInfo.padding) {
                layerData.textInfo.padding = {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                };
            }
        },
    };

    return behaviors;
};
